import { ChangeDetectorRef, Component, EventEmitter, Input, Output } from '@angular/core';
import { take } from 'rxjs';
import { UserService } from '../services/user.service';

/**
 * Minimal shape needed to determine whether an entity is shared with the
 * current user's customer/agency.
 */
export interface SharableEntity {
  ccCustomer: string;
  ccAgency: string;
}

/**
 * Base class for "view" components that display a single read-only entity
 * (premise hazards, premise hazard types, etc.) alongside Back/Edit/Delete
 * controls and shared-record detection.
 */
@Component({
  template: '' // Abstract component, no template
})
export abstract class BaseViewComponent {
  @Input() CanEdit: boolean = false;
  @Input() CanDelete: boolean = false;

  @Output() backClicked: EventEmitter<void> = new EventEmitter<void>();
  @Output() editClicked: EventEmitter<string | undefined> = new EventEmitter<string | undefined>();
  @Output() deleteClicked: EventEmitter<void> = new EventEmitter<void>();
  @Output() isSharedEvent: EventEmitter<boolean> = new EventEmitter<boolean>();

  public isShared: boolean = false;

  protected eTag?: string;
  private lastLoadedId?: string;

  constructor(
    protected readonly userService: UserService,
    protected readonly cdr: ChangeDetectorRef,
  ) {}

  public editButtonClicked(): void {
    this.editClicked.emit(this.eTag);
  }

  /**
   * Determines whether the given entity belongs to another customer/agency,
   * sets `isShared` accordingly, and emits `isSharedEvent`.
   */
  protected setSharingInfo(item: SharableEntity | undefined): void {
    if (!item) {
      return;
    }
    this.userService.UserInformation.pipe(take(1)).subscribe(userInfo => {
      if (!userInfo || !userInfo.customerId) {
        return;
      }
      const customerEquals = item.ccCustomer.localeCompare(userInfo.customerId, undefined, { sensitivity: 'accent' }) === 0;
      const agencyEquals = item.ccAgency.localeCompare(userInfo.agencyId, undefined, { sensitivity: 'accent' }) === 0;
      this.isShared = userInfo.isSuperAdmin ? false : !(customerEquals && agencyEquals);
      this.isSharedEvent.emit(this.isShared);
      this.cdr.detectChanges();
    });
  }

  /**
   * Records the given id as loaded and returns whether the caller should
   * actually (re)fetch it — false if the id hasn't changed since the last
   * load, or if the record is shared (we don't have access to re-fetch
   * records owned by another customer/agency).
   */
  protected shouldLoadItem(id: string): boolean {
    if (id === this.lastLoadedId) {
      return false;
    }
    this.lastLoadedId = id;
    return !this.isShared;
  }
}
